import clsx from 'clsx';
import { Skeleton } from './Skeleton';

/**
 * SkeletonCard - Stats card skeleton (CardStats için)
 */
export function SkeletonStatsCard({ className }) {
    return (
        <div className={clsx(
            'bg-bg-secondary rounded-xl border border-border-light p-4',
            className
        )}>
            <div className="flex items-center justify-between">
                <div className="space-y-2 flex-1">
                    <Skeleton variant="text" className="h-3 w-20" />
                    <Skeleton variant="text" className="h-7 w-24" />
                </div>
                <Skeleton variant="rounded" className="w-10 h-10" />
            </div>
        </div>
    );
}

/**
 * SkeletonCard - Genel kart skeleton
 */
export function SkeletonCard({
    hasHeader = true,
    hasFooter = false,
    lines = 3,
    className
}) {
    return (
        <div className={clsx(
            'bg-bg-secondary rounded-xl border border-border-light overflow-hidden',
            className
        )}>
            {hasHeader && (
                <div className="p-4 border-b border-border-light flex items-center gap-3">
                    <Skeleton variant="rounded" className="w-10 h-10" />
                    <div className="flex-1 space-y-2">
                        <Skeleton variant="text" className="h-4 w-32" />
                        <Skeleton variant="text" className="h-3 w-24" />
                    </div>
                </div>
            )}
            <div className="p-4 space-y-3">
                {Array.from({ length: lines }).map((_, i) => (
                    <Skeleton
                        key={i}
                        variant="text"
                        className={clsx('h-4', i === lines - 1 ? 'w-2/3' : 'w-full')}
                    />
                ))}
            </div>
            {hasFooter && (
                <div className="p-4 border-t border-border-light flex justify-end gap-2">
                    <Skeleton variant="rounded" className="h-8 w-20" />
                    <Skeleton variant="rounded" className="h-8 w-24" />
                </div>
            )}
        </div>
    );
}

/**
 * SkeletonBlockCard - Blok kartları için skeleton
 */
export function SkeletonBlockCard({ className }) {
    return (
        <div className={clsx(
            'bg-bg-secondary rounded-xl border border-border-light overflow-hidden',
            className
        )}>
            {/* Header */}
            <div className="p-4 pb-3 border-b border-border-light flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Skeleton variant="rounded" className="w-10 h-10" />
                    <div className="space-y-1.5">
                        <Skeleton variant="text" className="h-4 w-20" />
                        <Skeleton variant="text" className="h-3 w-28" />
                    </div>
                </div>
                <Skeleton variant="rounded" className="w-8 h-8" />
            </div>

            {/* Stats Grid */}
            <div className="p-4 space-y-4">
                <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="p-2 bg-bg-tertiary/50 rounded-lg text-center">
                            <Skeleton variant="text" className="h-5 w-8 mx-auto mb-1" />
                            <Skeleton variant="text" className="h-2 w-12 mx-auto" />
                        </div>
                    ))}
                </div>

                {/* Info Row */}
                <div className="flex justify-between">
                    <Skeleton variant="text" className="h-3 w-16" />
                    <Skeleton variant="text" className="h-3 w-16" />
                </div>

                {/* Progress */}
                <div>
                    <div className="flex justify-between mb-1.5">
                        <Skeleton variant="text" className="h-3 w-12" />
                        <Skeleton variant="text" className="h-3 w-8" />
                    </div>
                    <Skeleton variant="rounded" className="h-2 w-full" />
                </div>
            </div>
        </div>
    );
}

/**
 * SkeletonProjeKarti - İnşaat kartları için skeleton
 */
export function SkeletonProjeKarti({ className }) {
    return (
        <div className={clsx(
            'bg-bg-secondary rounded-2xl border border-border-light overflow-hidden',
            className
        )}>
            {/* Top accent */}
            <Skeleton variant="rectangular" className="h-1 w-full" animate={false} />

            <div className="p-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-start gap-4">
                        <Skeleton variant="rounded" className="w-12 h-12" />
                        <div className="space-y-2">
                            <Skeleton variant="text" className="h-5 w-36" />
                            <Skeleton variant="text" className="h-3 w-28" />
                        </div>
                    </div>
                    <Skeleton variant="rounded" className="h-6 w-24" />
                </div>

                {/* Progress */}
                <div className="mb-5">
                    <div className="flex justify-between mb-2">
                        <Skeleton variant="text" className="h-3 w-16" />
                        <Skeleton variant="text" className="h-3 w-8" />
                    </div>
                    <Skeleton variant="rounded" className="h-2.5 w-full" />
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-4 gap-3 mb-5">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="text-center p-3 bg-bg-tertiary/50 rounded-xl">
                            <Skeleton variant="text" className="h-6 w-8 mx-auto mb-1" />
                            <Skeleton variant="text" className="h-2 w-12 mx-auto" />
                        </div>
                    ))}
                </div>

                {/* Financial Row */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-bg-tertiary/50 rounded-xl mb-4">
                    <div>
                        <Skeleton variant="text" className="h-2 w-16 mb-1" />
                        <Skeleton variant="text" className="h-5 w-20" />
                    </div>
                    <div>
                        <Skeleton variant="text" className="h-2 w-20 mb-1" />
                        <Skeleton variant="text" className="h-5 w-16" />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-between">
                    <Skeleton variant="text" className="h-3 w-24" />
                    <Skeleton variant="text" className="h-3 w-16" />
                </div>
            </div>
        </div>
    );
}

export default SkeletonCard;
